// react
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

// components
import { Button, Divider, Input } from '@/components/atoms';

/**
 * InviteKeyRegistrationForm is a component that allows registering with an invite key or domain.
 */
export const InviteKeyRegistrationForm = ({
  onSubmit,
  inviteKeyLabel,
  isEmailRegistration,
}: InviteKeyRegistrationFormProps) => {
  // state
  const [email, setEmail] = useState('');
  const [inviteKey, setInviteKey] = useState('');

  // methods
  const handleInviteSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onSubmit(inviteKey, '');
    },
    [inviteKey, onSubmit],
  );

  const handleEmailSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onSubmit('', email);
    },
    [email, onSubmit],
  );

  return (
    <>
      {' '}
      <StyledInviteKeyRegistrationForm onSubmit={handleInviteSubmit}>
        <StyledTopSection>
          <StyledInputFields>
            <Input
              ariaLabel='invite key input field'
              type='text'
              label={inviteKeyLabel}
              isHideTitle={false}
              value={inviteKey}
              onChange={setInviteKey}
              placeholder={inviteKeyLabel}
            />
          </StyledInputFields>
        </StyledTopSection>
        <StyledButton ariaLabel='register' type='submit'>
          Register with {inviteKeyLabel}
        </StyledButton>
      </StyledInviteKeyRegistrationForm>
      {isEmailRegistration && (
        <>
          <StyledDivider />
          <StyledInviteKeyRegistrationForm onSubmit={handleEmailSubmit}>
            <StyledTopSection>
              <StyledInputFields>
                <Input
                  ariaLabel='email input field'
                  type='text'
                  label='Work email address'
                  isHideTitle={false}
                  value={email}
                  onChange={setEmail}
                  placeholder='example@mycompany.com'
                />
              </StyledInputFields>
            </StyledTopSection>
            <StyledButton ariaLabel='register' type='submit'>
              Register with Email
            </StyledButton>
          </StyledInviteKeyRegistrationForm>
        </>
      )}
    </>
  );
};

const StyledInviteKeyRegistrationForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
  align-items: center;
`;

const StyledInputFields = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
`;

const StyledTopSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledDivider = styled(Divider)`
  margin: ${(props) => props.theme.spacing[12]} 0;
  font: ${(props) => props.theme.typography.body.lg.regular};
`;

interface InviteKeyRegistrationFormProps {
  inviteKeyLabel: string;
  isEmailRegistration?: boolean;
  onSubmit: (inviteKey: string, email: string) => void;
}

InviteKeyRegistrationForm.displayName = 'InviteKeyRegistrationForm';

export default InviteKeyRegistrationForm;
